/** Chart-of-accounts data access and the structural guards the API also enforces. */

import type { Language } from '../../i18n/catalogs';

export type JsonFetch = <T>(path: string, init?: RequestInit) => Promise<T>;

/** Async callback used by the view props; kept here so the JSX files stay free of type arrows. */
export type AsyncVoid<T> = (value: T) => Promise<void>;

export interface AccountDimensionRule {
  slot: number;
  dimType: string;
  required: boolean;
  turnoverOnly: boolean;
}

export interface AccountNode {
  id: string;
  code: string;
  parentId: string | null;
  nameAz: string;
  nameEn: string;
  nameRu: string;
  kind: 'A' | 'P' | 'AP';
  isGroup: boolean;
  isOffbalance: boolean;
  currencyTracked: boolean;
  qtyTracked: boolean;
  isActive: boolean;
  dimensionRules: AccountDimensionRule[];
  level: number;
  children: AccountNode[];
}

export interface CreateAccountInput {
  code: string;
  parentCode: string;
  nameI18n: { az: string; en: string; ru: string };
  kind?: 'A' | 'P' | 'AP';
  isGroup?: boolean;
  isOffbalance?: boolean;
}

export interface ChartImportEntry {
  row: number;
  code: string;
  action: 'create' | 'update' | 'unchanged';
  changes: string[];
}

export interface ChartImportRowError {
  row: number;
  column: string;
  message: string;
}

export interface ChartImportReport {
  dryRun: boolean;
  applied: boolean;
  entries: ChartImportEntry[];
  errors: ChartImportRowError[];
  summary: { create: number; update: number; unchanged: number; errors: number };
}

export function accountName(node: AccountNode, language: Language): string {
  if (language === 'en') return node.nameEn;
  if (language === 'ru') return node.nameRu;
  return node.nameAz;
}

/** spec §5 / AD-4: a leaf cannot receive children even in the UI. */
export function creationRefusal(node: AccountNode): string | null {
  return node.isGroup ? null : 'accounts.parentIsLeaf';
}

export function totalAccounts(tree: readonly AccountNode[]): number {
  let total = 0;
  const walk = (nodes: readonly AccountNode[]): void => {
    for (const node of nodes) {
      total += 1;
      walk(node.children);
    }
  };
  walk(tree);
  return total;
}

function matches(node: AccountNode, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') return true;
  return [node.code, node.nameAz, node.nameEn, node.nameRu].some((value) =>
    value.toLocaleLowerCase().includes(needle),
  );
}

/** Keeps a match and its whole branch, so filtering never orphans a child. */
export function filterTree(tree: readonly AccountNode[], query: string): AccountNode[] {
  if (query.trim() === '') return [...tree];
  const keep = (nodes: readonly AccountNode[]): AccountNode[] => {
    const result: AccountNode[] = [];
    for (const node of nodes) {
      const children = keep(node.children);
      if (matches(node, query)) {
        result.push(node);
      } else if (children.length > 0) {
        result.push({ ...node, children });
      }
    }
    return result;
  };
  return keep(tree);
}

export function expandableCodes(tree: readonly AccountNode[]): string[] {
  const codes: string[] = [];
  const walk = (nodes: readonly AccountNode[]): void => {
    for (const node of nodes) {
      if (node.children.length > 0) {
        codes.push(node.code);
        walk(node.children);
      }
    }
  };
  walk(tree);
  return codes;
}

export interface ScreenProblem {
  status: number;
  code: string;
  detail?: string;
}

/** Reads both response shapes the platform emits: problem+json and the legacy `{error, message}`. */
export async function readProblem(response: Response): Promise<ScreenProblem> {
  let payload: Record<string, unknown> = {};
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    payload = {};
  }
  const code =
    typeof payload.code === 'string'
      ? payload.code
      : typeof payload.error === 'string'
        ? payload.error
        : `http.${String(response.status)}`;
  const detail =
    typeof payload.detail === 'string'
      ? payload.detail
      : typeof payload.message === 'string'
        ? payload.message
        : undefined;
  return { status: response.status, code, ...(detail === undefined ? {} : { detail }) };
}

export function problemMessage(problem: ScreenProblem): string {
  return problem.detail === undefined ? problem.code : `${problem.code}: ${problem.detail}`;
}

/** Wraps the authenticated fetch into the prompt-agnostic `JsonFetch` the API helpers take. */
export function createJsonFetch(
  authorizedFetch: (path: string, init?: RequestInit) => Promise<Response>,
): JsonFetch {
  return async <T,>(path: string, init?: RequestInit): Promise<T> => {
    const response = await authorizedFetch(path, init);
    if (!response.ok) {
      throw new Error(problemMessage(await readProblem(response)));
    }
    return (await response.json()) as T;
  };
}

export async function loadAccountsTree(fetchJson: JsonFetch): Promise<AccountNode[]> {
  return fetchJson<AccountNode[]>('/accounts/tree');
}

export async function createAccount(fetchJson: JsonFetch, input: CreateAccountInput): Promise<AccountNode> {
  return fetchJson<AccountNode>('/accounts', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function importChart(
  fetchJson: JsonFetch,
  input: { content: string; fileName: string; encoding?: 'utf8' | 'base64' },
  dryRun: boolean,
): Promise<ChartImportReport> {
  return fetchJson<ChartImportReport>(`/accounts/import?dryRun=${dryRun ? 'true' : 'false'}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      content: input.content,
      fileName: input.fileName,
      ...(input.encoding === undefined ? {} : { encoding: input.encoding }),
    }),
  });
}
